import {
  InputAdornment,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from '@mui/material'
import { useState } from 'react'
import { toTamil, toTamilName } from '@/lib/tamil'

/**
 * The Tamil name of something, typed without leaving the keyboard.
 *
 * <p>Two ways in, because both are wanted. In <b>தமிழ்</b> the box holds Tamil
 * and anything pasted from elsewhere goes straight in, which is what somebody
 * with an input tool of their own already does. In <b>En</b> the box takes what
 * is typed on an English keyboard and the Tamil appears under it as it is
 * written - "idli" becomes இட்லி - and that is what is saved.</p>
 *
 * <p>The conversion happens here, in the browser. No dish name is sent anywhere
 * to be turned into Tamil, and the office with its internet down can still
 * name a dish.</p>
 */
export function TamilNameField({
  value,
  onChange,
  label = 'Name in Tamil',
  helperText = 'Printed on the Tamil menu sheet',
  fromEnglish,
}: {
  value: string
  onChange: (value: string) => void
  label?: string
  helperText?: string
  /** The English name, offered as a starting point rather than imposed. */
  fromEnglish?: string
}) {
  const [mode, setMode] = useState<'ta' | 'en'>('ta')
  // Only lives while English is being typed, so the two can never disagree:
  // switching back to Tamil shows what was actually saved.
  const [roman, setRoman] = useState('')

  const typing = mode === 'en'
  const preview = typing ? toTamil(roman) : ''

  const suggestion = fromEnglish ? toTamilName(fromEnglish) : ''
  const worthOffering = Boolean(suggestion) && suggestion !== value && !typing

  return (
    <Stack spacing={0.5}>
    <TextField
      fullWidth
      label={label}
      value={typing ? roman : value}
      onChange={(event) => {
        const next = event.target.value
        if (typing) {
          setRoman(next)
          onChange(toTamil(next))
        } else {
          onChange(next)
        }
      }}
      placeholder={typing ? 'idli' : 'இட்லி'}
      helperText={
        typing
          ? preview
            ? `${preview} — this is what is saved`
            : 'Type it as it sounds: aa ee oo for the long vowels, kootu, vadai'
          : helperText
      }
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <Tooltip title="Type in Tamil, or type in English and have it written in Tamil">
              <ToggleButtonGroup
                size="small"
                exclusive
                value={mode}
                onChange={(_event, next) => {
                  if (!next) return
                  setMode(next)
                  // Starting to type in English starts from what is there, so
                  // the box is not silently emptied under a name already given.
                  if (next === 'en') setRoman('')
                }}
              >
                <ToggleButton value="ta" sx={{ px: 1, py: 0.25 }}>
                  தமிழ்
                </ToggleButton>
                <ToggleButton value="en" sx={{ px: 1, py: 0.25 }}>
                  En
                </ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          </InputAdornment>
        ),
      }}
    />
      {/* Offered, not written: the English name is a decent start for most
          dishes and wrong for a few, and which is which is the office's to
          say. */}
      {worthOffering ? (
        <Link
          component="button"
          type="button"
          variant="caption"
          underline="hover"
          sx={{ alignSelf: 'flex-start' }}
          onClick={() => {
            setMode('ta')
            onChange(suggestion)
          }}
        >
          {value ? 'Replace with' : 'Use'} {suggestion}
        </Link>
      ) : null}
    </Stack>
  )
}
